import { ListStore } from '@deqo/foundation';
import { invokeLater, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { AlertBody, CompiledAlertBody } from './AlertBody';

export interface AlertDefinition {
    readonly id?: string;
    readonly type: string;
    readonly body: AlertBody;
    readonly delay: number;
    readonly modal?: boolean;
}

export interface AlertInstance {
    readonly id: string;
    readonly type: string;
    readonly body: CompiledAlertBody;
    readonly delay: number;
    readonly modal?: boolean;
}

export interface AlertHandle {
    close(): void;
    waitForClose(): Promise<void>;
}

export class AlertManager extends ListStore<AlertInstance> {

    public invoke(alertDef: AlertDefinition): AlertHandle {

        const inst = this.createInstance(alertDef);
        const instClose = () => this.close(inst.id);

        this.updateState(s => {
            // If modal, replace all with new
            if (inst.modal) {
                s.splice(0, s.length, inst);
            }
            // If not, replace all except modal with new
            else {
                s.splice(0, s.length, ...s.filter(x => !!x.modal), inst);
            }
        });

        if (alertDef.delay) {
            invokeLater(alertDef.delay, instClose);
        }

        return {
            close: instClose,
            waitForClose: () => this.waitForInstanceClose(inst),
        };
    }

    public closeAll(): void {
        this.updateState(s => {
            s.splice(0, s.length);
        });
    }

    public close(id: string): void {
        this.updateState(s => {
            const i = s.findIndex(x => x.id === id);
            if (i >= 0) s.splice(i, 1);
        });
    }

    private createInstance({ id, body, ...props }: AlertDefinition) {
        return {
            id: id ?? uuid(),
            ...props,
            body: AlertBody.compile(body),
        };
    }

    private exists(id: string) {
        return !!this.find(x => x.id === id);
    }

    private waitForInstanceClose(inst: AlertInstance) {
        if (!this.exists(inst.id)) {
            return Promise.resolve();
        }
        return new Promise<void>((rs) => {
            this.observe(() => {
                if (!this.exists(inst.id)) {
                    rs();
                }
            });
        });
    }
}
